// MfaEnrollmentCard — TOTP-based two-factor auth enrollment.
//
// Uses Supabase Auth's built-in MFA API (supabase.auth.mfa.*). One
// TOTP factor per user; enrollment flow is:
//
//   1. Click "Set up 2FA" → auth.mfa.enroll({factorType:'totp'})
//      returns {id, totp:{qr_code, secret, uri}}.
//   2. Display the QR + secret for the authenticator app
//      (Google Authenticator, 1Password, Authy, etc.) to scan.
//   3. User enters the 6-digit code the app generates →
//      auth.mfa.challenge → auth.mfa.verify. On success the factor
//      is committed and the session upgrades to AAL2.
//   4. Card re-renders showing "2FA active" with an Unenroll button.
//
// Login-time challenge is handled by Supabase automatically once the
// user has a verified TOTP factor — the sign-in flow already emits
// a challenge, but the login UI needs matching handling. For Phase
// 1 (this component) we ship the enrollment surface; challenge-on-
// login is tracked as a follow-up (Supabase-js does most of the
// work, so it's a small integration).
//
// Audit 2026-07-01 S-004 / missing-features list #1.

import React from 'react';
import { supabase } from '../lib/supabase';

const STATE = {
  LOADING:  'loading',
  IDLE:     'idle',       // no factor, not enrolling
  ENROLLING: 'enrolling', // factor created, showing QR + code entry
  ENROLLED: 'enrolled',   // verified factor exists
  ERROR:    'error',
};

export default function MfaEnrollmentCard({ showToast = () => {} }) {
  const [state, setState] = React.useState(STATE.LOADING);
  const [error, setError] = React.useState(null);
  const [enrollment, setEnrollment] = React.useState(null);   // { id, totp: {qr_code, secret} }
  const [code, setCode] = React.useState('');
  const [factors, setFactors] = React.useState([]);
  const [busy, setBusy] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setState(STATE.LOADING);
    try {
      const { data, error: e } = await supabase.auth.mfa.listFactors();
      if (e) throw e;
      // Supabase separates factors into totp + phone arrays and also
      // returns a flat `all` array. Prefer `all` for a simple list.
      const list = data?.all || [...(data?.totp || [])];
      setFactors(list);
      const verified = list.filter(f => f.status === 'verified');
      setState(verified.length > 0 ? STATE.ENROLLED : STATE.IDLE);
      setError(null);
    } catch (e) {
      setError(e?.message || String(e));
      setState(STATE.ERROR);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const beginEnroll = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
      if (e) throw e;
      // Some Supabase deploys reuse an existing unverified factor;
      // regardless, the response carries the QR + secret we need.
      setEnrollment(data);
      setState(STATE.ENROLLING);
    } catch (e) {
      setError(e?.message || String(e));
      showToast(`Enrollment failed: ${e?.message || 'unknown'}`, 'error');
    } finally { setBusy(false); }
  };

  const cancelEnroll = async () => {
    if (!enrollment?.id) { setState(STATE.IDLE); setEnrollment(null); return; }
    setBusy(true);
    try {
      await supabase.auth.mfa.unenroll({ factorId: enrollment.id });
    } catch { /* fall through — factor may already be gone */ }
    setEnrollment(null);
    setCode('');
    await refresh();
    setBusy(false);
  };

  const verifyEnroll = async (e) => {
    e?.preventDefault?.();
    if (!enrollment?.id) return;
    const trimmed = String(code).replace(/\D/g, '');
    if (trimmed.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const chal = await supabase.auth.mfa.challenge({ factorId: enrollment.id });
      if (chal.error) throw chal.error;
      const ver = await supabase.auth.mfa.verify({
        factorId:    enrollment.id,
        challengeId: chal.data.id,
        code:        trimmed,
      });
      if (ver.error) throw ver.error;
      setEnrollment(null);
      setCode('');
      showToast('Two-factor authentication is now active on your account.');
      await refresh();
    } catch (e) {
      setError(e?.message || 'Code did not verify. Try again.');
    } finally { setBusy(false); }
  };

  const unenroll = async (factorId) => {
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: 'Turn off two-factor authentication?',
      body: 'Your account will lose the extra layer of protection.',
      confirmLabel: 'Turn off 2FA', danger: true,
    }) : Promise.resolve(window.confirm('Turn off two-factor authentication?')));
    if (!ok) return;
    setBusy(true);
    try {
      const { error: e } = await supabase.auth.mfa.unenroll({ factorId });
      if (e) throw e;
      showToast('Two-factor authentication turned off.');
      await refresh();
    } catch (e) {
      showToast(`Turn-off failed: ${e?.message || 'unknown'}`, 'error');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-bold">Two-factor authentication</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Adds a second sign-in step from your authenticator app (Google Authenticator, 1Password, Authy, etc.). Recommended for accounts holding client PII.
          </p>
        </div>
        {state === STATE.ENROLLED && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Active</span>
        )}
      </div>

      {state === STATE.LOADING && (
        <div className="text-sm text-slate-500">Checking 2FA status…</div>
      )}

      {state === STATE.ERROR && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-800">
          Couldn't load 2FA state: {error}
        </div>
      )}

      {state === STATE.IDLE && (
        <div className="space-y-3">
          <div className="text-xs text-slate-600">
            Your account isn't using 2FA yet. Click below to start setup — it takes about a minute.
          </div>
          <button
            onClick={beginEnroll}
            disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            {busy ? 'Preparing…' : 'Set up 2FA'}
          </button>
        </div>
      )}

      {state === STATE.ENROLLING && enrollment && (
        <form onSubmit={verifyEnroll} className="space-y-4">
          <div className="text-xs text-slate-700 leading-relaxed">
            Scan this QR code with your authenticator app, then enter the 6-digit code it generates.
          </div>
          {enrollment.totp?.qr_code && (
            <div className="flex flex-col items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-4">
              {/*
                Supabase returns the QR as either an SVG string, a
                data URL, or an http(s) URL. Always render through
                <img> — for the SVG-string case we wrap it in a
                data URL, which puts the SVG in an image sandbox
                where <script>/<foreignObject>/onload can't fire.
                The earlier version dropped raw SVG in via
                dangerouslySetInnerHTML, which would execute any
                script the SVG carried. The QR value isn't
                user-controlled today (Supabase server generates it
                for the caller's own enrollment) but defense in
                depth is free and the comment above no longer lies.
              */}
              <img
                src={
                  enrollment.totp.qr_code.startsWith('data:') || /^https?:\/\//i.test(enrollment.totp.qr_code)
                    ? enrollment.totp.qr_code
                    : `data:image/svg+xml;base64,${btoa(enrollment.totp.qr_code)}`
                }
                alt="2FA QR code"
                className="w-40 h-40"
              />
              {enrollment.totp.secret && (
                <div className="text-[11px] text-slate-500 font-mono break-all text-center">
                  Or paste this key manually:<br/>
                  <span className="text-slate-800 select-all">{enrollment.totp.secret}</span>
                </div>
              )}
            </div>
          )}
          <div>
            <label className="block">
              <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">6-digit code from your app</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                value={code}
                onChange={e => { setCode(e.target.value); setError(null); }}
                placeholder="123456"
                maxLength={6}
                className={`mt-1 w-full border rounded-lg px-3 py-2 text-lg font-mono tracking-widest text-center ${error ? 'border-red-400' : 'border-slate-200'}`}
              />
            </label>
            {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy || code.length < 6}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-semibold px-4 py-2 rounded-lg"
            >
              {busy ? 'Verifying…' : 'Verify + activate'}
            </button>
            <button
              type="button"
              onClick={cancelEnroll}
              disabled={busy}
              className="text-sm text-slate-500 hover:text-slate-900 font-medium"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {state === STATE.ENROLLED && (
        <div className="space-y-3">
          <div className="text-xs text-slate-600">
            2FA is active. You'll be asked for a 6-digit code from your authenticator app when signing in.
          </div>
          <div className="space-y-1">
            {factors.filter(f => f.status === 'verified').map(f => (
              <div key={f.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <div>
                  <div className="text-sm font-semibold text-slate-800">
                    {f.friendly_name || (f.factor_type === 'totp' ? 'Authenticator app' : f.factor_type)}
                  </div>
                  {f.created_at && (
                    <div className="text-[11px] text-slate-500">
                      Enrolled {new Date(f.created_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => unenroll(f.id)}
                  disabled={busy}
                  className="text-xs text-red-700 hover:text-red-900 font-semibold"
                >
                  Turn off
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
